import { ID, TaskFilterOptions, TaskLinkIndex } from '@griply/types';
import { buildTaskLinkIndex, narrowTasksByLinkIndex } from '@griply/utils';
import { useMemo, useRef } from 'react';

// The index is rebuilt per published list, but a bucket whose tasks did not change keeps its
// array — so a screen bound to one goal skips the work below.
//
// The ref is read and written inside the memo, i.e. during render, which React forbids in
// general. It is safe only because `buildTaskLinkIndex` treats `previous` as an identity hint:
// a stale or discarded one can cost a reuse opportunity, never change a bucket's contents. Do
// not add logic that keys behaviour on `previous`.
const EMPTY: never[] = [];

export const useTasksNarrowedByLinks = <T extends { id: ID }>(
  tasks: T[] | undefined,
  filters: TaskFilterOptions[],
): T[] => {
  const previousIndex = useRef<TaskLinkIndex<T>>();

  return useMemo(() => {
    const list: T[] = tasks ?? EMPTY;
    const index = buildTaskLinkIndex(list, previousIndex.current);
    previousIndex.current = index;

    return narrowTasksByLinkIndex(list, filters, index);
  }, [tasks, filters]);
};
